/// <amd-module name="DistributedTaskUI/SecurityComponent/PipelinePermissionsAuthorization/Stores/PipelinesAuthorizationStore" />
import { PipelinesAuthorizationActionsHub } from "PipelinePermissionsAuthorization/Actions/PipelinesAuthorizationActionsHub";
import { ResourcePipelinePermissions, PipelinePermission } from "PipelineChecks/Authorization/Client/Contracts/PipelinePermissions";
import * as VSSStore from "VSS/Reflux/Store";
import { BuildDefinitionReference } from "DistributedTaskUI/SecurityComponent/PipelinePermissionsAuthorization/Source/Build/Contracts/Build";

export interface IPipelinesAuthorizationState {
    authorizedDefinitions: ResourcePipelinePermissions;
    authorizedPipelineDefinitions: BuildDefinitionReference[];
    allPipelineDefinitions: BuildDefinitionReference[];
    authorizedPipelinesDictionary: { [pipelineId: string]: boolean };
    errorMessage: string;
}

export class PipelinesAuthorizationStore extends VSSStore.Store {
    constructor(actionsHub: PipelinesAuthorizationActionsHub) {
        super("PipelinesAuthorizationStore");
        this._state = PipelinesAuthorizationStore.getDefaultState();
        this._actions = actionsHub;
        this._actions.toggleAllPipelinesRestriction.addListener(this._toggleAllPipelinesRestriction);
        this._actions.initializeAuthorizedDefinitions.addListener(this._initializeAuthorizedDefinitions);
        this._actions.initializeAllPipelineDefinitions.addListener(this._initializeAllPipelineDefinitions);
        this._actions.initializeAuthorizedPipelineDefinitions.addListener(this._initializeAuthorizedPipelineDefinitions);
        this._actions.setErrorMessage.addListener(this._setErrorMessage);
    }

    public dispose() {
        this._actions.toggleAllPipelinesRestriction.removeListener(this._toggleAllPipelinesRestriction);
        this._actions.initializeAuthorizedDefinitions.removeListener(this._initializeAuthorizedDefinitions);
        this._actions.initializeAllPipelineDefinitions.removeListener(this._initializeAllPipelineDefinitions);
        this._actions.initializeAuthorizedPipelineDefinitions.removeListener(this._initializeAuthorizedPipelineDefinitions);
        this._actions.setErrorMessage.removeListener(this._setErrorMessage);
    }

    public getState(): IPipelinesAuthorizationState {
        return this._state;
    }

    public static getDefaultState(): IPipelinesAuthorizationState {
        return {
            authorizedDefinitions: null,
            authorizedPipelineDefinitions: [],
            allPipelineDefinitions: [],
            authorizedPipelinesDictionary: {},
            errorMessage: ""
        };
    }

    private _toggleAllPipelinesRestriction = (authorizedDefinitions: ResourcePipelinePermissions): void => {
        this._initializeAuthorizedDefinitions(authorizedDefinitions);
        this._clearDeletedPipelines();

        this.emitChanged();
    }

    private _initializeAuthorizedDefinitions = (authorizedDefinitions: ResourcePipelinePermissions): void => {
        this._state.authorizedDefinitions = authorizedDefinitions;
        this._state.errorMessage = "";
        this._state.authorizedPipelinesDictionary = {};
        if (this._state.authorizedDefinitions.pipelines) {
            this._state.authorizedDefinitions.pipelines.forEach((pipeline) => {
                this._state.authorizedPipelinesDictionary[pipeline.id] = true;
            });
        }

        this.emitChanged();
    }

    private _initializeAllPipelineDefinitions = (buildDefinitions: BuildDefinitionReference[]): void => {
        this._state.allPipelineDefinitions = buildDefinitions;
        this._state.errorMessage = "";

        this.emitChanged();
    }

    private _initializeAuthorizedPipelineDefinitions = (buildDefinitions: BuildDefinitionReference[]): void => {
        this._state.authorizedPipelineDefinitions = buildDefinitions;
        this._state.errorMessage = "";
        this._clearDeletedPipelines();

        this.emitChanged();
    }

    private _clearDeletedPipelines(): void {
        if (this._state.authorizedDefinitions && this._state.authorizedDefinitions.pipelines) {
            // Remove deleted definitions
            this._state.authorizedDefinitions.pipelines = this._state.authorizedDefinitions.pipelines.filter((authorizedDefinition: PipelinePermission) => {
                return this._state.authorizedPipelineDefinitions.find((definition: BuildDefinitionReference) => {
                    return definition.id === authorizedDefinition.id;
                });
            });
        }
    }

    private _setErrorMessage = (error: string): void => {
        this._state.errorMessage = error;

        this.emitChanged();
    }

    private _state: IPipelinesAuthorizationState;
    private _actions: PipelinesAuthorizationActionsHub;
}
